import React, { useState, useEffect, useCallback, useRef } from "react";
import ActionDropdown from "../common/ui/actiondropdown";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import { useLoader } from "../../context/loadercontext";
import Pagination from "../common/table/tablepagination";
import { useNavigate } from "react-router-dom";
import { RiDeleteBin5Line } from "react-icons/ri";
import { MdEditSquare } from "react-icons/md";
import toast from "react-hot-toast";
import { DeletePreviewModal, FinalDeleteConfirmModal } from "../common/ui/deletemodal";
import { useAuth } from "../../context/AuthContext"; // Auth context
import NoFound from "../common/ui/NoFound";
import WarningModal from "../common/ui/warningmodel";

export default function DispatchViewTable({ searchTerm = "", dispatchstartDate = "", dispatchendDate = "", selectedCustomer = "" }) {
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  const [tableData, setTableData] = useState([]);
  const logout = useAuth();
  // const [openRow, setOpenRow] = useState({ cardIndex: null, rowIndex: null });

  const [showDeletePreview, setShowDeletePreview] = useState(false);
  const [showFinalDeleteConfirm, setShowFinalDeleteConfirm] = useState(false);
  const [deleteMode, setDeleteMode] = useState("parent"); // parent | child
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [parentIndex, setParentIndex] = useState(null);
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${day} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatLabel = (value) => {
    return value
      ? value
        .toLowerCase()
        .split(",")
        .map(part =>
          part
            .trim()
            .split(" ")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
        )
        .join(", ")
      : "";
  };

  // const handleToggle = (cardIndex, rowIndex) => {
  //   setOpenRow(
  //     openRow.cardIndex === cardIndex && openRow.rowIndex === rowIndex
  //       ? { cardIndex: null, rowIndex: null }
  //       : { cardIndex, rowIndex }
  //   );
  // };

  const handleEdit = (parentId, childId) => {
    navigate("/dispatch/edit-dispatch", {
      state: { parentId, childId }
    });
  };

  const fetchController = useRef(null);

  const fetchDispatchData = useCallback(async () => {
    setLoading(true);
    if (fetchController.current) fetchController.current.abort();
    fetchController.current = new AbortController();
    const signal = fetchController.current.signal;
    try {
      const url = new URL(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.GET_ALL}`);
      // Append search filters
      if (searchTerm.trim() !== "") url.searchParams.append("number", searchTerm);
      if (dispatchstartDate) url.searchParams.append("start_date", dispatchstartDate);
      if (dispatchendDate) url.searchParams.append("end_date", dispatchendDate);
      if (selectedCustomer) url.searchParams.append("customer_name", selectedCustomer);
      const callApi = async () =>
        await fetch(url.toString(), {
          method: "GET",
          headers: getHeaders(true),
          signal,
        });

      // Initial API call
      let response = await callApi();

      // -------------------------
      // 🔥 Handle 401 token expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry the same API call
          response = await callApi();
        } else {
          console.error("Refresh token failed. Logging out...");
          logout();
          return;
        }
      }

      const result = await response.json();
      if (result.status === 200 && Array.isArray(result.data)) {
        const mappedData = result.data.map((item) => ({
          id: item.id,
          dispatchID: item.dispatch_id,
          createdAt: item.created_at,
          totalDispatchWeight: item.total_dispatch_weight || 0,
          child: (item.child || []).map((c) => ({
            id: c.id,
            exbondID: c.exbond_id,
            beNumber: c.be_number,
            dispatchDate: c.dispatch_date,
            dispatchWeight: c.dispatch_weight.toFixed(2) || 0,
            truckNumber: c.truck_no,
            material: c.material,
            exbondWeight: c.exbond_weight.toFixed(2) || 0,
            customer: c.customer,
            material_short_code: c.material_short_code,
          })),
        }));
        setTableData(mappedData);
      } else {
        setTableData([]);
      }
    } catch (error) {
      console.error(error);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, dispatchstartDate, dispatchendDate, selectedCustomer, setLoading, logout]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDispatchData();
    }, 300); // debounce

    return () => clearTimeout(timer);
  }, [searchTerm, dispatchstartDate, dispatchendDate, selectedCustomer, fetchDispatchData]);

  // Delete handlers
  const handleDeleteParent = (parentId) => {
    setDeleteMode("parent");
    setDeleteIndex(parentId);   // store ID
    setParentIndex(null);
    setShowDeletePreview(true);
  };


  const handleDeleteChild = (parentId, childId) => {
    setDeleteMode("child");
    setParentIndex(parentId);   // store parent ID
    setDeleteIndex(childId);    // store child ID
    setShowDeletePreview(true);
  };

  const handleCancelPreview = () => {
    setShowDeletePreview(false);
    setDeleteIndex(null);
    setParentIndex(null);
  };

  const handleProceedToFinalConfirm = () => {
    setShowDeletePreview(false);
    setShowFinalDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      if (deleteMode === "parent") {
        // Find parent index by ID
        const parentIdx = tableData.findIndex(d => d.id === deleteIndex);
        const dispatchId = tableData[parentIdx]?.id;
        if (!dispatchId)
          return
        setWarningMessage("Invalid Dispatch ID");
        setWarningVisible(true);
        let res = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.SOFT_DELETE_COMPLETE(dispatchId)}`,
          { method: "PUT", headers: getHeaders(true) }
        );

        // -------------------------
        // 🔥 If ACCESS TOKEN expired
        // -------------------------
        if (res.status === 401) {
          console.warn("Access token expired. Attempting refresh...");

          const newToken = await refreshAccessToken();

          if (newToken) {
            // Retry API call with new token
            res = await fetch(
              `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.SOFT_DELETE_COMPLETE(dispatchId)}`,
              { method: "PUT", headers: getHeaders(true) }
            );
          } else {
            console.error("Refresh token failed. Logging out...");
            logout(); // redirect user
            return;
          }
        }

        const result = await res.json().catch(() => ({}));

        if (res.ok && result.status === 200) {
          toast.success(result.message || "Dispatch deleted successfully");
          await fetchDispatchData();
        } else {
          setWarningMessage("Failed to delete dispatch");
          setWarningVisible(true);
        }

      } else if (deleteMode === "child") {
        // Find parent index
        const parentIdx = tableData.findIndex(d => d.id === parentIndex);
        if (parentIdx === -1) return;

        // Find child index inside parent's child array
        const childIdx = tableData[parentIdx].child.findIndex(c => c.id === deleteIndex);
        if (childIdx === -1) return;


        const childId = tableData[parentIdx].child[childIdx].id;
        let res = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.SOFT_DELETE_PARTIAL(childId)}`,
          { method: "PUT", headers: getHeaders(true) }
        );

        // -------------------------
        // 🔥 If ACCESS TOKEN expired
        // -------------------------
        if (res.status === 401) {
          console.warn("Access token expired. Attempting refresh...");

          const newToken = await refreshAccessToken();

          if (newToken) {
            // Retry API call with new token
            res = await fetch(
              `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.SOFT_DELETE_PARTIAL(childId)}`,
              { method: "PUT", headers: getHeaders(true) }
            );
          } else {
            console.error("Refresh token failed. Logging out...");
            logout(); // redirect user
            return;
          }
        }

        const result = await res.json().catch(() => ({}));

        if (res.ok && result.status === 200) {
          toast.success(result.message || "Child entry deleted");
          await fetchDispatchData();
        } else {
          setWarningMessage("Failed to delete child entry");
          setWarningVisible(true);
        }
      }
    } catch (err) {
      console.error(err);
      setWarningMessage("Unexpected error while deleting");
      setWarningVisible(true);
    } finally {
      setShowFinalDeleteConfirm(false);
      setDeleteIndex(null);
      setParentIndex(null);
      setLoading(false);
    }
  };


  // Pagination
  const totalPages = Math.ceil(tableData.length / rowsPerPage);
  const paginatedData = tableData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const handlePageChange = (page) => { if (page >= 1 && page <= totalPages) setCurrentPage(page); };

  return (
    <>
      <div className="mt-4 space-y-3">
        {paginatedData.length === 0 ? (
          <div className="p-3 w-full">
            <div className="overflow-x-auto">
              <table className="w-full text-table text-center table-fixed min-w-[500px]">
                <thead className="h-10 text-gray-500">
                  <tr>
                    <th>Exbond No</th>
                    <th>BI Number</th>
                    <th>Dispatch Date</th>
                    <th>Dispatch Weight (kg)</th>
                    <th>Truck No</th>
                    <th>Material</th>
                    <th>Weight (kg)</th>
                    <th>Customer</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={8} className="p-6 text-gray-400">
                      <NoFound />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          paginatedData.map((card, cardIndex) => (
            <React.Fragment key={cardIndex}>
              <div className="p-2 w-full md:grid md:grid-cols-12 md:gap-2">
                <div className="hidden md:block md:col-span-1 text-[11px] font-medium text-gray-700">
                  <div className="mb-2 mt-1">
                    <div className="font-semibold bg-[#dbf6e5] text-[#25915a] w-16 h-6 flex items-center justify-center rounded shadow-sm text-[12px]">
                      DI {card.dispatchID}
                    </div>
                    <div className="mt-1 text-xs text-gray-900">{formatDate(card.createdAt)}</div>
                    <div className="font-semibold bg-[#fff1d6] text-[#ce880d] w-16 h-6 flex items-center justify-center rounded shadow-sm text-[12px]">
                      {card.totalDispatchWeight?.toLocaleString()} kg
                    </div>
                  </div>
                </div>

                <div className="md:col-span-11 text-[11px] text-gray-700 w-full overflow-x-auto">
                  <table className="min-w-[950px] w-full text-[13px] text-center">
                    <thead className="border-b border-gray-200 text-gray-600 font-medium">
                      <tr>
                        <th>Exbond No</th>
                        <th>BI Number</th>
                        <th>Dispatch Date</th>
                        <th>Dispatch Weight (kg)</th>
                        <th>Truck No</th>
                        <th>Material</th>
                        <th>Weight (kg)</th>
                        <th>Customer</th>
                        <th>
                          <ActionDropdown
                            actions={{ edit: true, delete: true }}
                            onEdit={() => handleEdit(card.id, 0)}
                            onDelete={() => handleDeleteParent(card.id)}
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {card.child.map((row, idx) => (
                        <tr key={idx} className="cursor-pointer hover:bg-gray-50">
                          <td>EI {row.exbondID}</td>
                          <td>{row.beNumber}</td>
                          <td>{formatDate(row.dispatchDate)}</td>
                          <td>{row.dispatchWeight?.toLocaleString()}</td>
                          <td>{row.truckNumber.toUpperCase()}</td>
                          <td>{row.material_short_code.toUpperCase()}</td>
                          <td>{row.exbondWeight?.toLocaleString()}</td>
                          <td>{row.customer ? row.customer.charAt(0).toUpperCase() + row.customer.slice(1) : ""}</td>
                          <td className="flex justify-center gap-1">
                            {/* Child Edit button */}
                            <button
                              id={row.id}
                              onClick={() => handleEdit(card.id, row.id)}
                              className="text-green-500 hover:text-green-700 p-1"
                              title="Edit"
                            >
                              <MdEditSquare size={18} />
                            </button>
                            <button
                              id={row.id}
                              className="text-red-500 p-1"
                              title="Delete"
                              onClick={() => handleDeleteChild(card.id, row.id)}
                            >
                              <RiDeleteBin5Line size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="border-t border-dashed border-gray-300 w-full"></div>
            </React.Fragment>
          ))
        )}
      </div>

      {tableData.length > rowsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          totalItems={tableData.length}
        />
      )}

      {showDeletePreview && deleteIndex !== null && (
        <DeletePreviewModal
          title="Dispatch Details"
          data={
            deleteMode === "parent"
              ? (() => {
                const parent = tableData.find(d => d.id === deleteIndex);
                return parent
                  ? {
                    "DI No": parent.dispatchID,
                    "Total Weight": parent.totalDispatchWeight?.toLocaleString() + " kg",
                    "Created At": formatDate(parent.createdAt),
                  }
                  : {};
              })()
              : (() => {
                const parent = tableData.find(d => d.id === parentIndex);
                const child = parent?.child?.find(c => c.id === deleteIndex);
                return child
                  ? {
                    "Exbond No": child.exbondID,
                    "BE Number": child.beNumber,
                    "Dispatch Date": formatDate(child.dispatchDate),
                    "Dispatch Weight": child.dispatchWeight?.toLocaleString() + " kg",
                    "Truck No": child.truckNumber.toUpperCase(),
                    "Material": child.material_short_code.toUpperCase(),
                    "Weight": child.exbondWeight?.toLocaleString() + " kg",
                    "Customer": child.customer ? formatLabel(child?.customer) : "—",
                  }
                  : {};
              })()
          }
          onCancel={handleCancelPreview}
          onProceed={handleProceedToFinalConfirm}
        />
      )}
      {showFinalDeleteConfirm && (
        <FinalDeleteConfirmModal
          title="Confirm Delete"
          message="Are you sure you want to permanently delete this section?"
          onCancel={() => setShowFinalDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
      {warningVisible && (
        <WarningModal
          message={warningMessage}
          onClose={() => setWarningVisible(false)}
        />
      )}

    </>
  );
}
